import type { PaidPlan } from '@/types/billing-plan';

import { PAID_PLANS, PRO_PLAN_CONTACT_EMAIL } from '@/types/billing-plan';
import { isBotLogin } from '@/utils/github/bot-detection';

export const PRO_PLAN_ACTIVE_DEVELOPER_MIN_PRS = 15;
export const TRIAL_QUOTA_ACTIVE_DEVELOPER_MIN_PRS = 5;
export const PRO_PLAN_ACTIVITY_WINDOW_DAYS = 30;

export type MemberPrStatisticsLike = Record<
  string,
  {
    last30DaysPRCount: number;
  }
>;

/**
 * Counts developers considered "active" for Pro seat minimums.
 * Active means opening at least PRO_PLAN_ACTIVE_DEVELOPER_MIN_PRS PRs
 * within the rolling PRO_PLAN_ACTIVITY_WINDOW_DAYS day window.
 */
export function countActiveDevelopersForProSeatMinimum(
  memberStatistics: MemberPrStatisticsLike | null | undefined,
): number {
  return Object.entries(memberStatistics ?? {}).filter(
    ([login, member]) =>
      !isBotLogin(login) && member.last30DaysPRCount >= PRO_PLAN_ACTIVE_DEVELOPER_MIN_PRS,
  ).length;
}

export function countDevelopersWithRecentPrs(
  memberStatistics: MemberPrStatisticsLike | null | undefined,
): number {
  return Object.entries(memberStatistics ?? {}).filter(
    ([login, member]) => !isBotLogin(login) && member.last30DaysPRCount > 0,
  ).length;
}

export function countActiveDevelopersForTrialQuota(
  memberStatistics: MemberPrStatisticsLike | null | undefined,
): number {
  return Object.entries(memberStatistics ?? {}).filter(
    ([login, member]) =>
      !isBotLogin(login) && member.last30DaysPRCount >= TRIAL_QUOTA_ACTIVE_DEVELOPER_MIN_PRS,
  ).length;
}

export function buildProSeatMinimumViolationMessage({
  plan,
  requiredSeats,
  selectedSeats,
}: {
  plan: PaidPlan;
  requiredSeats: number;
  selectedSeats: number;
}): string {
  const seatWord = requiredSeats === 1 ? 'seat' : 'seats';
  return `${PAID_PLANS[plan].name} requires at least ${requiredSeats} ${seatWord} for this installation. You selected ${selectedSeats}. Because codebase scans run on the whole repository, every active developer (opened at least ${PRO_PLAN_ACTIVE_DEVELOPER_MIN_PRS} PRs in the last ${PRO_PLAN_ACTIVITY_WINDOW_DAYS} days) needs a seat. If you have a special situation, please contact ${PRO_PLAN_CONTACT_EMAIL}.`;
}

export function buildProSeatMinimumUnavailableMessage(plan: PaidPlan): string {
  return `Could not verify the ${PAID_PLANS[plan].name} seat minimum from the last ${PRO_PLAN_ACTIVITY_WINDOW_DAYS} days of PR activity. Please try again.`;
}
